/* eslint-disable prettier/prettier */
import React, { useCallback, useState, useEffect } from 'react';

import { Container, Button } from 'react-bootstrap';

import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';

import { formatPrice } from '../../../utils/funcoes';

import Loading from '../../Loading';
import getResponseErrors from '../../../utils/getResponseErrors';

interface IPlanoSimulado {
  PlaCodigo: number;
  PlaValor: number;
  PlaValorFormatado: string;
  PlaMeses: number;
}

interface IReturnSimulacao {
  plano: IPlanoSimulado;
  valorMulta: number;
  geraNovoContrato: boolean;
}

type ComponentProps = {
  PlaCodigo: number;
  DepCodigo: number;
  updated_at: Date;
  rotaExpirado: boolean;
  handleFecharComponent(load: boolean): void;
};

const CartaoSaudeRemoveDependente: React.FC<ComponentProps> = ({
  PlaCodigo,
  DepCodigo,
  updated_at,
  rotaExpirado,
  handleFecharComponent,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [dadosSimulados, SetDadosSimulados] = useState<
    | {
      plano: IPlanoSimulado;
      valorMulta: string;
      geraMulta: boolean;
      geraNovoContrato: boolean;
    }
    | undefined
  >(undefined);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IReturnSimulacao>(
          `planos/simulaexclusaodependente/${PlaCodigo}?DepCodigo=${DepCodigo}&updated_at=${updated_at}`,
        );

        const { plano, valorMulta, geraNovoContrato } = response.data;

        SetDadosSimulados({
          plano: { ...plano, PlaValorFormatado: formatPrice(plano.PlaValor) },
          valorMulta: formatPrice(valorMulta),
          geraNovoContrato,
          geraMulta: !!valorMulta,
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível simular adição de dependentes',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    loadDados();
  }, [PlaCodigo, updated_at, addToast, DepCodigo]);

  const handleDeleteDependente = useCallback(async () => {
    setLoading(true);
    try {
      const rota = rotaExpirado ? 'dependentesexpirado' : 'dependentes';
      await api.delete(
        `planos/${rota}/${PlaCodigo}?DepCodigo=${DepCodigo}&updated_at=${updated_at}`,
      );
      handleFecharComponent(true);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível excluir o dependente',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [
    rotaExpirado,
    PlaCodigo,
    DepCodigo,
    updated_at,
    handleFecharComponent,
    addToast,
  ]);

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}
      {dadosSimulados && (
        <>
          <div className="my-4">
            {dadosSimulados.geraMulta && (
              <h5>
                O valor da Multa de exclusão do dependente será de{' '}
                <b className="text-danger" style={{ fontSize: '1.8rem' }}>
                  {dadosSimulados.valorMulta}
                </b>
              </h5>
            )}
            <p>
              Valor do plano após a exclusão:{' '}
              <b>{dadosSimulados.plano.PlaValorFormatado}</b>
            </p>
            <h6 className="mt-4">Essa operação não poderá ser desfeita.</h6>
            {dadosSimulados.geraNovoContrato && (
              <div className="alert alert-warning px-3 py-1 mt-2">
                <h5>Atenção!</h5>
                Essa operação írá gerar um novo contrato, começando da parcela 1
                até a {dadosSimulados.plano.PlaMeses}.
              </div>
            )}
            <Button
              type="submit"
              className="mt-2"
              variant="danger"
              onClick={handleDeleteDependente}
              disabled={loading}
            >
              Prosseguir com a exclusão
            </Button>
            <Button
              type="button"
              className="mt-2 ml-3"
              variant="outline-secondary"
              onClick={() => handleFecharComponent(false)}
              disabled={loading}
            >
              Voltar
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default CartaoSaudeRemoveDependente;
